import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Eye, Printer, Loader2, Download, Trash2, Pencil } from "lucide-react";
import api, { formatApiError, API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import Pagination from "@/components/Pagination";
import { formatDateTime } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const STATUS_OPTIONS = [
  { value: "draft", label: "مسودة", cls: "bg-muted text-muted-foreground" },
  { value: "submitted", label: "مقدمة", cls: "bg-info/10 text-info" },
  { value: "pending", label: "قيد الانتظار", cls: "bg-amber-100 text-amber-700" },
  { value: "completed", label: "منجز", cls: "bg-success/10 text-success" },
  { value: "approved", label: "معتمدة", cls: "bg-emerald-100 text-emerald-800" },
  { value: "rejected", label: "مرفوضة", cls: "bg-destructive/10 text-destructive" },
];
const statusInfo = (s) => STATUS_OPTIONS.find((x) => x.value === s) || STATUS_OPTIONS[1];

export default function SubmissionsPage() {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const limit = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (search.trim()) params.q = search.trim();
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await api.get("/submissions", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [page, search, statusFilter]);
  useEffect(() => { load(); }, [load]);

  const canPrint = hasPermission("submissions.print");
  const canChangeStatus = user?.is_superadmin || hasPermission("submissions.update") || hasPermission("submissions.view_all");
  const canDelete = hasPermission("submissions.delete");

  const updateStatus = async (id, value) => {
    try {
      await api.put(`/submissions/${id}/status`, { status: value });
      toast.success("تم تحديث الحالة");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (s) => {
    if (!window.confirm(`حذف الاستمارة ${s.form_number}؟`)) return;
    try {
      await api.delete(`/submissions/${s.id}`);
      toast.success("تم الحذف");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const exportCSV = () => {
    // download with cookie auth
    const url = `${API}/submissions/export.csv`;
    window.open(url, "_blank");
  };

  return (
    <div data-testid="submissions-page">
      <div className="flex items-center justify-between mb-6 gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">الاستمارات المقدمة</h1>
          <p className="text-muted-foreground mt-1">جميع الاستمارات مع البحث والتصفية والطباعة</p>
        </div>
        {user?.is_superadmin && (
          <Button onClick={exportCSV} variant="outline" data-testid="export-csv-button">
            <Download className="w-4 h-4 ms-1" /> تصدير CSV
          </Button>
        )}
      </div>

      <div className="gov-card mb-4 p-3 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="absolute top-1/2 -translate-y-1/2 right-3 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="submissions-search"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="ابحث برقم الاستمارة (مثال: PS-2026-000123)"
            className="ps-3 pe-10 h-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger data-testid="status-filter" className="w-44">
            <SelectValue placeholder="كل الحالات" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">كل الحالات</SelectItem>
            {STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="gov-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-start px-4 py-3 font-medium">رقم الاستمارة</th>
              <th className="text-start px-4 py-3 font-medium">النموذج</th>
              <th className="text-start px-4 py-3 font-medium">المُقدِّم</th>
              <th className="text-start px-4 py-3 font-medium w-40">الحالة</th>
              <th className="text-start px-4 py-3 font-medium">التاريخ</th>
              <th className="text-start px-4 py-3 font-medium w-32">إجراءات</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-4 py-12 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>}
            {!loading && items.map((s) => {
              const st = statusInfo(s.status);
              return (
                <tr key={s.id} className="border-b border-border/60 last:border-0 hover:bg-surface/40" data-testid={`submission-row-${s.form_number}`}>
                  <td className="px-4 py-2.5 font-mono font-medium text-primary">{s.form_number}</td>
                  <td className="px-4 py-2.5">{s.template_title}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{s.submitted_by_name}</td>
                  <td className="px-4 py-2.5">
                    {canChangeStatus ? (
                      <Select value={s.status} onValueChange={(v) => updateStatus(s.id, v)}>
                        <SelectTrigger data-testid={`status-select-${s.form_number}`} className={`h-7 text-xs font-medium ${st.cls} border-0 px-2`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${st.cls}`}>{st.label}</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground font-mono text-xs">
                    {formatDateTime(s.submitted_at)}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1">
                      <button onClick={() => navigate(`/print/${s.id}`)} data-testid={`view-submission-${s.form_number}`} className="p-1.5 rounded hover:bg-surface-hover text-muted-foreground hover:text-foreground" title="عرض">
                        <Eye className="w-4 h-4" />
                      </button>
                      {s.status === "draft" && (
                        <button
                          onClick={() => navigate(`/forms/edit/${s.id}`)}
                          data-testid={`edit-submission-${s.form_number}`}
                          className="p-1.5 rounded hover:bg-amber-50 text-muted-foreground hover:text-amber-700"
                          title="تعديل المسودة"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      )}
                      {canPrint && (
                        <button onClick={() => navigate(`/print/${s.id}?print=1`)} data-testid={`print-submission-${s.form_number}`} className="p-1.5 rounded hover:bg-surface-hover text-muted-foreground hover:text-foreground" title="طباعة PDF">
                          <Printer className="w-4 h-4" />
                        </button>
                      )}
                      {canDelete && (
                        <button onClick={() => remove(s)} data-testid={`delete-submission-${s.form_number}`} className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive" title="حذف">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {!loading && !items.length && (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">لا توجد استمارات</td></tr>
            )}
          </tbody>
        </table>
        <Pagination page={page} total={total} limit={limit} onPageChange={setPage} testid="submissions-pagination" />
      </div>
    </div>
  );
}
